<template>
	<div class="quantity-container">
		<button
			class="quantity-btn quantity-minus"
			@click="decrease"
			:disabled="modelValue <= min"
			type="button"
		>
			&minus;
		</button>
		<input
			type="number"
			class="quantity-input"
			:value="modelValue"
			@input="updateValue"
			:min="min"
			:max="max"
			:step="step"
		/>
		<button
			class="quantity-btn quantity-plus"
			@click="increase"
			:disabled="max !== undefined && modelValue >= max"
			type="button"
		>
			&plus;
		</button>

		<button
			class="btn btn-primary add-to-cart"
			@click="increase"
			v-if="modelValue === 0 && showAddToCart"
		>
			Add to cart
		</button>
	</div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

export default defineComponent({
	name: 'QuantityComponent',
	props: {
		modelValue: {
			type: Number,
			default: 1,
		},
		min: {
			type: Number,
			default: 0,
		},
		max: {
			type: Number,
			default: undefined,
		},
		step: {
			type: Number,
			default: 1,
		},
		showAddToCart: {
			type: Boolean,
			default: false,
		},
	},
	emits: ['update:modelValue'],
	methods: {
		updateValue(event: Event) {
			const target = event.target as HTMLInputElement;
			let value = parseInt(target.value, 10);

			if (isNaN(value)) {
				value = this.min;
			}

			// Ensure value is within bounds
			if (value < this.min) {
				value = this.min;
			}
			if (this.max !== undefined && value > this.max) {
				value = this.max;
			}

			this.$emit('update:modelValue', value);
		},
		decrease() {
			const newValue = this.modelValue - this.step;
			if (newValue >= this.min) {
				this.$emit('update:modelValue', newValue);
			}
		},
		increase() {
			const newValue = this.modelValue + this.step;
			if (this.max === undefined || newValue <= this.max) {
				this.$emit('update:modelValue', newValue);
			}
		},
	},
});
</script>

<style scoped lang="scss">
.quantity-container {
	display: inline-flex;
	position: relative;
	align-items: center;
	border: 1px solid #dddddd;
	border-radius: var(--rs-border-radius);
	background: white;
	overflow: hidden;
}

.quantity-btn {
	display: flex;
	justify-content: center;
	align-items: center;
	transition: background-color 0.15s ease-in-out;
	cursor: pointer;
	border: none;
	background: #f8f9fa;
	width: 40px;
	height: 40px;
	font-weight: bold;
	font-size: 18px;

	&:hover:not(:disabled) {
		background: #e9ecef;
	}

	&:active:not(:disabled) {
		background: #dee2e6;
	}

	&:disabled {
		cursor: not-allowed;
		background: #f8f9fa;
		color: #adb5bd;
	}
}

.quantity-minus {
	border-top-left-radius: var(--rs-border-radius);
	border-bottom-left-radius: var(--rs-border-radius);
}

.quantity-plus {
	border-top-right-radius: var(--rs-border-radius);
	border-bottom-right-radius: var(--rs-border-radius);
}

.quantity-input {
	outline: none;
	border: none;
	background: white;
	width: 60px;
	height: 40px;
	color: rgb(var(--rs-typography-font-body-color));
	font-size: 16px;
	text-align: center;

	/* Hide number input spinners */
	&::-webkit-outer-spin-button,
	&::-webkit-inner-spin-button {
		appearance: none;
		margin: 0;
	}

	&[type='number'] {
		appearance: textfield;
	}
}

.add-to-cart {
	--rs-btn-padding-side: 10px;

	position: absolute;
	inset: 0;
	border-radius: 0;
}
</style>
